import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

/**
 * ArrayEditor — no-code CRUD for a section's array field.
 *
 * Detects the shape of each item from the first row (or a supplied
 * `template` prop) and renders Input/Textarea/color/image controls
 * per field. Long strings (or well-known keys) become Textareas.
 * Nested objects/arrays fall back to a compact JSON textarea.
 *
 * Handles Add / Delete / Reorder inside a single component.
 */
export default function ArrayEditor({
  label,
  value = [],           // array of items
  onChange,             // (nextValue) => void
  template,             // { field: '' } — shape used when adding a new row
  itemLabelKey = "title",// which field to show as the row header
  testIdPrefix = "arr",
}) {
  const [openIdx, setOpenIdx] = useState(0);

  // Detect field shape from template first, else first row, else {}
  const shape = template || value[0] || {};
  const fields = Object.keys(shape);

  const update = (i, field, v) =>
    onChange(value.map((row, j) => (j === i ? { ...row, [field]: v } : row)));

  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= value.length) return;
    const arr = [...value];
    [arr[i], arr[t]] = [arr[t], arr[i]];
    onChange(arr);
    setOpenIdx(t);
  };

  const addRow = () => {
    const blank = {};
    for (const k of fields) {
      const src = shape[k];
      if (Array.isArray(src))       blank[k] = [];
      else if (typeof src === "number") blank[k] = 0;
      else if (typeof src === "boolean") blank[k] = false;
      else if (src && typeof src === "object") blank[k] = {};
      else                             blank[k] = "";
    }
    onChange([...value, blank]);
    setOpenIdx(value.length);
  };

  const removeRow = (i) => {
    if (!window.confirm(`Delete this ${label.replace(/s$/, "") || "item"}?`)) return;
    onChange(value.filter((_, j) => j !== i));
  };

  return (
    <div className="border rounded-lg bg-slate-50 p-4 mb-3" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-bold">{label}</Label>
        <Button size="sm" variant="outline" onClick={addRow} data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1"/> Add
        </Button>
      </div>

      {value.length === 0 && (
        <div className="text-xs text-slate-500 italic py-4 text-center">
          No items yet — click <span className="font-semibold">Add</span> to create the first one.
        </div>
      )}

      <div className="space-y-2">
        {value.map((row, i) => {
          const open = openIdx === i;
          const header = row[itemLabelKey] || row.name || row.label || row.title || row.headline || `Item ${i + 1}`;
          return (
            <div key={i} className="bg-white border rounded" data-testid={`${testIdPrefix}-row-${i}`}>
              <div className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="text-[11px] font-mono text-slate-400 w-6">#{i + 1}</span>
                <button type="button" className="flex-1 text-left truncate hover:text-cyan-700" onClick={() => setOpenIdx(open ? -1 : i)}>
                  {typeof header === "string" ? header : `Item ${i + 1}`}
                </button>
                <button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="w-4 h-4"/></button>
                <button type="button" onClick={() => move(i, 1)}  className="text-slate-400 hover:text-slate-700"><ChevronDown className="w-4 h-4"/></button>
                <button type="button" onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>

              {open && (
                <div className="p-3 border-t bg-slate-50/50 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fields.map((f) => (
                    <FieldControl
                      key={f}
                      name={f}
                      value={row[f]}
                      onChange={(v) => update(i, f, v)}
                      testId={`${testIdPrefix}-row-${i}-${f}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * FieldControl — picks the right widget based on the value shape / key name.
 */
function FieldControl({ name, value, onChange, testId }) {
  const isLongText = typeof value === "string" && (value.length > 90 || /^(body|description|desc|summary|sub|subtitle|content|about|lead)$/i.test(name));
  const isBullets  = Array.isArray(value) && value.length && typeof value[0] === "string";
  const isColorHex = /^color$|_color$|^bg$|^fg$/i.test(name) && (typeof value === "string");
  const isImageUrl = /image_url|logo_url|image$|avatar|photo/i.test(name);
  const isNumber   = typeof value === "number";
  const isNested   = value !== null && typeof value === "object" && !Array.isArray(value);
  const isNestedArr = Array.isArray(value) && (!value.length || typeof value[0] === "object");

  const wrapper = (child) => (
    <div className={isLongText || isBullets || isNested || isNestedArr ? "md:col-span-2" : ""} data-testid={`${testId}-wrap`}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{name}</Label>
      {child}
    </div>
  );

  if (isImageUrl) {
    return wrapper(
      <ImageUploader
        value={value || ""}
        onChange={onChange}
        category="cms"
        label={name}
        preview="wide"
        testId={testId}
      />
    );
  }
  if (isColorHex) {
    return wrapper(
      <div className="flex gap-2 items-center">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 rounded border"/>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>
      </div>
    );
  }
  if (isNumber) {
    return wrapper(<Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value) || 0)} data-testid={testId}/>);
  }
  if (isBullets) {
    return wrapper(
      <Textarea
        rows={4}
        value={(value || []).join("\n")}
        onChange={(e) => onChange(e.target.value.split(/\n/).filter((s) => s.trim().length))}
        placeholder="One bullet per line"
        data-testid={testId}
      />
    );
  }
  if (isLongText) {
    return wrapper(<Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>);
  }
  if (isNested || isNestedArr) {
    // Compact JSON escape hatch for deeply nested structures.
    return wrapper(
      <Textarea
        rows={5}
        className="font-mono text-xs"
        value={JSON.stringify(value ?? (isNestedArr ? [] : {}), null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { /* keep old until valid */ }
        }}
        data-testid={testId}
      />
    );
  }
  return wrapper(<Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>);
}
